import React, { useEffect, useState } from 'react'

import { Container } from './styles'
import Box from '@mui/material/Box'
import { Button, Card, Chip } from '@mui/material'
import { useModalContext } from '../../contexts/ModalContext'
import DataTable from 'react-data-table-component'
import DataTableExtensions from 'react-data-table-component-extensions'
import 'react-data-table-component-extensions/dist/index.css'
import * as NotificationService from '../../services/notifications'
import RuleFolderIcon from '@mui/icons-material/RuleFolder'
import DeleteIcon from '@mui/icons-material/Delete'
import ApproveOrDenyModal from './ApproveOrDenyModal'

const Status = {
    APPROVED: {
        text: 'APROVADO',
        color: '#2e7d32'
    },
    PENDING: {
        text: 'PENDENTE',
        color: '#1565c0'
    },
    DENIED: {
        text: 'NEGADO',
        color: '#c62828'
    }
}

const CampaignTypeLabel = {
    ONE_TIME: 'Única',
    CAMPAIGN_WINDOW: 'Janela'
}

function formatDate(dateStr) {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

const Notifications = () => {
    const { setModal } = useModalContext()
    const [data, setData] = useState([])

    useEffect(() => {
        fetchTableData()
    }, [])

    async function fetchTableData() {
        await NotificationService.getAll().then((response) => {
            setData(response)
        })
    }

    const handleDelete = async (id) => {
        if (!window.confirm('Tem certeza que deseja excluir esta campanha?')) return
        try {
            await NotificationService.deleteNotification(id)
            alert('Campanha excluída com sucesso!')
            fetchTableData()
        } catch {
            alert('Erro ao excluir campanha!')
        }
    }

    const columns = [
        {
            id: 'id',
            name: 'ID',
            selector: (row) => row.id,
            sortable: true,
            width: '70px'
        },
        {
            id: 'mechanicId',
            name: 'Mecânica',
            selector: (row) => row.mechanicId,
            sortable: true,
            width: '100px'
        },
        {
            name: 'Tipo',
            selector: (row) => row.campaignType,
            cell: (row) => (
                <Chip
                    label={CampaignTypeLabel[row.campaignType] || row.campaignType}
                    size='small'
                    color={row.campaignType === 'ONE_TIME' ? 'primary' : 'secondary'}
                    variant='outlined'
                />
            ),
            sortable: true,
            width: '100px'
        },
        {
            name: 'Título',
            selector: (row) => row.title,
            cell: (row) => <div style={{ whiteSpace: 'normal' }}>{row.title}</div>,
            width: '15%'
        },
        {
            name: 'Conteúdo',
            selector: (row) => row.text,
            cell: (row) => <div style={{ whiteSpace: 'normal', fontSize: '13px' }}>{row.text}</div>,
            width: '18%'
        },
        {
            name: 'Datas',
            cell: (row) => (
                <div style={{ whiteSpace: 'normal', fontSize: '12px', lineHeight: '1.5' }}>
                    {row.campaignType === 'ONE_TIME' ? (
                        row.scheduledDate ? (
                            <span>Agendado: {formatDate(row.scheduledDate)}</span>
                        ) : (
                            <span style={{ color: '#666' }}>Imediato (após aprovação)</span>
                        )
                    ) : (
                        <>
                            <div>Início: {formatDate(row.startDate)}</div>
                            <div>Fim: {formatDate(row.endDate)}</div>
                        </>
                    )}
                </div>
            ),
            width: '14%'
        },
        {
            name: 'Status',
            cell: (row) => {
                const status = Status[row.status] || { text: row.status, color: 'gray' }
                return (
                    <Chip
                        label={status.text}
                        size='small'
                        sx={{
                            backgroundColor: status.color,
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '11px'
                        }}
                    />
                )
            },
            width: '120px'
        },
        {
            name: 'Envios',
            cell: (row) => (
                <div style={{ fontSize: '11px', lineHeight: '1.6' }}>
                    {row.campaignType === 'ONE_TIME' ? (
                        <Chip
                            label={row.oneTimeSent ? 'Enviado' : 'Pendente'}
                            size='small'
                            color={row.oneTimeSent ? 'success' : 'default'}
                            variant={row.oneTimeSent ? 'filled' : 'outlined'}
                            sx={{ fontSize: '11px' }}
                        />
                    ) : (
                        <>
                            <div>
                                <Chip
                                    label={row.startNotificationSent ? 'Início ✓' : 'Início ✗'}
                                    size='small'
                                    color={row.startNotificationSent ? 'success' : 'default'}
                                    variant={row.startNotificationSent ? 'filled' : 'outlined'}
                                    sx={{ fontSize: '10px', mb: '2px' }}
                                />
                            </div>
                            <div>
                                <Chip
                                    label={row.reminderNotificationSent ? 'Lembrete ✓' : 'Lembrete ✗'}
                                    size='small'
                                    color={row.reminderNotificationSent ? 'success' : 'default'}
                                    variant={row.reminderNotificationSent ? 'filled' : 'outlined'}
                                    sx={{ fontSize: '10px' }}
                                />
                            </div>
                        </>
                    )}
                </div>
            ),
            width: '110px'
        },
        {
            name: 'Ações',
            cell: (row) => (
                <Box display='flex' gap='4px'>
                    <Button
                        variant='outlined'
                        size='small'
                        style={{ background: '#1565c0', color: 'white', minWidth: '36px' }}
                        onClick={() => setModal(<ApproveOrDenyModal updateTable={fetchTableData} notification={row} />)}
                        title='Gerenciar'
                    >
                        <RuleFolderIcon fontSize='small' />
                    </Button>
                    <Button
                        variant='outlined'
                        size='small'
                        style={{ background: '#c62828', color: 'white', minWidth: '36px' }}
                        onClick={() => handleDelete(row.id)}
                        title='Excluir'
                    >
                        <DeleteIcon fontSize='small' />
                    </Button>
                </Box>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '120px'
        }
    ]

    return (
        <Container>
            <Box mb={3} fontWeight='bold' fontSize={{ xs: 'h7.fontSize', sm: 'h6.fontSize', md: 'h5.fontSize' }}>
                Notificações de Mecânicas
            </Box>
            <Box mb={3} fontSize={{ xs: 'h9.fontSize', sm: 'h9.fontSize', md: 'h8.fontSize' }}>
                Campanhas de notificação enviadas por mecânicas para aprovação
            </Box>
            <Box mt={3} mb={5}>
                <Card>
                    <DataTableExtensions columns={columns} data={data} export={false} print={false}>
                        <DataTable
                            pagination
                            paginationPerPage={15}
                            highlightOnHover
                            defaultSortFieldId='id'
                            defaultSortAsc={false}
                        />
                    </DataTableExtensions>
                </Card>
            </Box>
        </Container>
    )
}

export default Notifications
